
import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation } from "@tanstack/react-query";
import { ErrorResponse } from "@/common/types/errorResponse";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { CompanyContractConsumptionControlledBy } from "@/features/company/types/contract.type";
import { ProductCategoryDistributionLimitControlledByCurrencyValidationSchema, ProductCategoryDistributionLimitControlledByQuantityValidationSchema, ProductCategoryDistributionLimitValidationSchema } from "../../schemas/distributionsLimits.schema";

interface UseCreateGovernmentAllocationDistributionLimitProps {
  governmentAllocationId?: string;
  productCategoryId?: string;
  controlledBy?: CompanyContractConsumptionControlledBy;
  onSuccess?: () => void;
  onError?: (error: ErrorResponse) => void;
}

export const useCreateGovernmentAllocationDistributionLimit = ({
  governmentAllocationId,
  productCategoryId,
  controlledBy = CompanyContractConsumptionControlledBy.Currency,
  onSuccess,
  onError,
}: UseCreateGovernmentAllocationDistributionLimitProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['createGovernmentAllocationDistributionLimit', governmentAllocationId, productCategoryId],
    mutationFn: (data: ProductCategoryDistributionLimitValidationSchema) => {
      let payload;

      if (controlledBy === CompanyContractConsumptionControlledBy.Currency) {
        data = data as ProductCategoryDistributionLimitControlledByCurrencyValidationSchema;

        payload = {
          product_category_id: productCategoryId,
          maximum_value: unFormatCurrency(data.maximum_value),
          product_distribution_limits_attributes: data.product_distribution_limits_attributes.map((productDistributionLimits) => ({
            product_id: productDistributionLimits.product.id,
            maximum_value: unFormatCurrency(productDistributionLimits.maximum_value),
          })),
        }
      } else {
        data = data as ProductCategoryDistributionLimitControlledByQuantityValidationSchema;

        payload = {
          product_category_id: productCategoryId,
          maximum_quantity: unFormatUnknownUnit(data.maximum_quantity),
          product_distribution_limits_attributes: data.product_distribution_limits_attributes.map((productDistributionLimits) => ({
            product_id: productDistributionLimits.product.id,
            maximum_quantity: unFormatUnknownUnit(productDistributionLimits.maximum_quantity),
          })),
        }
      }

      return privateAxios.put(`/billing/government_allocations/${governmentAllocationId}/update_distribution_limits`, {
        product_category_distribution_limits_attributes: [payload],
      });
    },
    onSuccess,
    onError,
  });
}
